import { animalAvatarGenerator } from "@/shared/lib/animal-avatar";
import { Flex, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import Animal from "react-animals";
import { twMerge } from "tailwind-merge";
import { ZoomableAnimal } from "./zoomable-animal";

type AnimalAvatarProps = {
  size: string;
  trackId: string;
  selected?: boolean;
  titleSize?: "1" | "2" | "3";
  hoverable?: boolean;
  showTitle?: boolean;
  wrappClassName?: string;
  enableHoverZoom?: boolean;
  zoomScale?: number;
  value?: {
    animal: string;
    color: string;
    colorHex: string;
    key: string;
  } | null;
  onClick?: () => void;
};

export function AnimalAvatar({
  trackId,
  size,
  value,
  selected,
  wrappClassName,
  showTitle,
  hoverable,
  titleSize = "2",
  enableHoverZoom,
  zoomScale = 4,
  onClick,
}: AnimalAvatarProps) {
  const animalAvatar = useMemo(() => {
    if (value) {
      return value;
    }

    return animalAvatarGenerator.getRandomAvatar(trackId);
  }, [trackId, value]);

  return (
    <div
      onClick={onClick}
      className={twMerge(
        `flex gap-2 justify-center items-center [&_img]:select-none [&_img]:pointer-events-none`,
        hoverable &&
          "[&_.v-animal-avatar]:border-2 [&_.v-animal-avatar]:border-transparent hover:[&_.v-animal-avatar]:border-2 hover:[&_.v-animal-avatar]:border-white",
        selected &&
          "[&_.v-animal-avatar]:border-2 [&_.v-animal-avatar]:border-red-500",
        wrappClassName
      )}
    >
      {animalAvatar ? (
        <>
          {enableHoverZoom ? (
            <ZoomableAnimal
              size={size}
              animal={animalAvatar.animal}
              zoomScale={zoomScale}
              color={animalAvatar.colorHex}
            />
          ) : (
            <Animal
              circle
              size={size}
              name={animalAvatar.animal}
              color={animalAvatar.colorHex}
            />
          )}
          {showTitle && (
            <Flex gap="1">
              <Text size={titleSize} className="capitalize">
                {animalAvatar.color}
              </Text>
              <Text size={titleSize} className="capitalize">
                {animalAvatar.animal}
              </Text>
            </Flex>
          )}
        </>
      ) : (
        <Flex gap="2" align="center">
          <div
            className="bg-red-500 text-black rounded-full flex justify-center items-center font-medium whitespace-nowrap"
            style={{
              width: size,
              minWidth: size,
              height: size,
              fontSize: `${parseInt(size) / 2.5}px`,
            }}
          >
            {trackId.slice(trackId.length - 4, trackId.length)}
          </div>
          {showTitle && (
            <Text size={titleSize} className="capitalize">
              {trackId}
            </Text>
          )}
        </Flex>
      )}
    </div>
  );
}
